import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Search,
  Download,
  FilterList,
  TrendingUp,
  TrendingDown,
  People,
  AttachMoney,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { format } from 'date-fns';

interface UserReport {
  id: number;
  email: string;
  fullName: string;
  credits: number;
  totalBets: number;
  totalWinnings: number;
  netResult: number;
  totalBetsCount: number;
  winningBetsCount: number;
  winRate: number;
  lastBetDate?: string;
  lastLoginAt?: string;
  registeredAt: string;
  isActive: boolean;
  isSuspended: boolean;
}

interface UserReportSummary {
  totalUsers: number;
  activeUsers: number;
  suspendedUsers: number;
  totalBetsAmount: number;
  totalWinningsAmount: number;
  totalNetResult: number;
  totalBetsCount: number;
  averageBetAmount: number;
  averageWinAmount: number;
  overallWinRate: number;
}

interface UserReportResponse {
  users: UserReport[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  summary: UserReportSummary;
}

interface UserReportFilters {
  searchTerm: string;
  startDate: Date | null;
  endDate: Date | null;
  isActive: boolean | null;
  isSuspended: boolean | null;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

const UserReports: React.FC = () => {
  const [filters, setFilters] = useState<UserReportFilters>({
    searchTerm: '',
    startDate: null,
    endDate: null,
    isActive: null,
    isSuspended: null,
    sortBy: 'TotalBets',
    sortDirection: 'desc',
    page: 1,
    pageSize: 20
  });

  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const { data: reportData, isLoading, error, refetch } = useQuery<UserReportResponse>({
    queryKey: ['user-reports', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      
      if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);
      if (filters.startDate) params.append('startDate', formatLocalDate(filters.startDate));
      if (filters.endDate) params.append('endDate', formatLocalDate(filters.endDate));
      if (filters.isActive !== null) params.append('isActive', filters.isActive.toString());
      if (filters.isSuspended !== null) params.append('isSuspended', filters.isSuspended.toString());
      params.append('sortBy', filters.sortBy);
      params.append('sortDirection', filters.sortDirection);
      params.append('page', filters.page.toString());
      params.append('pageSize', filters.pageSize.toString());

      const response = await axios.get(`/admin/users/reports?${params.toString()}`);
      return response.data;
    }
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return format(date, 'MMM dd, yyyy');
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const handleFilterChange = (key: keyof UserReportFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' && key !== 'pageSize' ? 1 : prev.page // Reset page when filters change
    }));
  };

  const handleSearch = () => {
    refetch();
  };

  const handleSort = (column: string) => {
    const isCurrentColumn = filters.sortBy === column;
    const newDirection = isCurrentColumn && filters.sortDirection === 'asc' ? 'desc' : 'asc';
    
    handleFilterChange('sortBy', column);
    handleFilterChange('sortDirection', newDirection);
    refetch();
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);
      if (filters.startDate) params.append('startDate', formatLocalDate(filters.startDate));
      if (filters.endDate) params.append('endDate', formatLocalDate(filters.endDate));
      if (filters.isActive !== null) params.append('isActive', filters.isActive.toString());
      if (filters.isSuspended !== null) params.append('isSuspended', filters.isSuspended.toString());
      
      const response = await axios.get(`/admin/users/reports/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `user-reports-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const getStatusChip = (user: UserReport) => {
    if (user.isSuspended) {
      return <Chip label="Suspended" color="error" size="small" />;
    }
    if (user.isActive) {
      return <Chip label="Active" color="success" size="small" />;
    }
    return <Chip label="Inactive" color="default" size="small" />;
  };

  const getNetResultColor = (netResult: number) => {
    if (netResult > 0) return 'success.main';
    if (netResult < 0) return 'error.main';
    return 'text.primary';
  };

  if (error) {
    return (
      <Alert severity="error">
        Failed to load user reports. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        User Betting Reports
      </Typography>

      {/* Summary Cards */}
      {reportData?.summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Total Users
                    </Typography>
                    <Typography variant="h5">
                      {reportData.summary.totalUsers.toLocaleString()}
                    </Typography>
                  </Box>
                  <People sx={{ fontSize: 40, color: 'primary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Total Bets
                    </Typography>
                    <Typography variant="h5">
                      {formatCurrency(reportData.summary.totalBetsAmount)}
                    </Typography>
                  </Box>
                  <TrendingDown sx={{ fontSize: 40, color: 'error.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Total Winnings
                    </Typography>
                    <Typography variant="h5">
                      {formatCurrency(reportData.summary.totalWinningsAmount)}
                    </Typography>
                  </Box>
                  <TrendingUp sx={{ fontSize: 40, color: 'success.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Net Result
                    </Typography>
                    <Typography 
                      variant="h5" 
                      color={getNetResultColor(reportData.summary.totalNetResult)}
                    >
                      {formatCurrency(reportData.summary.totalNetResult)}
                    </Typography>
                  </Box>
                  <AttachMoney sx={{ fontSize: 40, color: 'info.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Search Users"
              placeholder="Email, name..."
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <DatePicker
              label="Start Date"
              value={filters.startDate}
              onChange={(date) => handleFilterChange('startDate', date)}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <DatePicker
              label="End Date"
              value={filters.endDate}
              onChange={(date) => handleFilterChange('endDate', date)}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.isActive === null ? '' : filters.isActive ? 'active' : 'inactive'}
                label="Status"
                onChange={(e) => {
                  const value = e.target.value as string;
                  handleFilterChange('isActive', value === '' ? null : value === 'active');
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={filters.sortBy}
                label="Sort By"
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              >
                <MenuItem value="TotalBets">Total Bets</MenuItem>
                <MenuItem value="TotalWinnings">Total Winnings</MenuItem>
                <MenuItem value="Email">Email</MenuItem>
                <MenuItem value="LastLoginAt">Last Login</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 1 }}>
            <Button
              variant="contained"
              onClick={handleSearch}
              startIcon={<Search />}
              fullWidth
            >
              Search
            </Button>
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExport}
            size="small"
          >
            Export CSV
          </Button>
        </Box>
      </Paper>

      {/* Data Table */}
      <Paper>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={filters.sortBy === 'Email'}
                        direction={filters.sortBy === 'Email' ? filters.sortDirection : 'asc'}
                        onClick={() => handleSort('Email')}
                      >
                        User
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Credits</TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={filters.sortBy === 'TotalBets'}
                        direction={filters.sortBy === 'TotalBets' ? filters.sortDirection : 'asc'}
                        onClick={() => handleSort('TotalBets')}
                      >
                        Total Bets
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={filters.sortBy === 'TotalWinnings'}
                        direction={filters.sortBy === 'TotalWinnings' ? filters.sortDirection : 'asc'}
                        onClick={() => handleSort('TotalWinnings')}
                      >
                        Total Winnings
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">Net Result</TableCell>
                    <TableCell align="center">Bets Count</TableCell>
                    <TableCell align="center">Win Rate</TableCell>
                    <TableCell align="center">Last Bet</TableCell>
                    <TableCell align="center">
                      <TableSortLabel
                        active={filters.sortBy === 'LastLoginAt'}
                        direction={filters.sortBy === 'LastLoginAt' ? filters.sortDirection : 'asc'}
                        onClick={() => handleSort('LastLoginAt')}
                      >
                        Last Login
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData?.users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {user.fullName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {getStatusChip(user)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(user.credits)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold">
                          {formatCurrency(user.totalBets)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold" color="success.main">
                          {formatCurrency(user.totalWinnings)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          fontWeight="bold" 
                          color={getNetResultColor(user.netResult)}
                        >
                          {formatCurrency(user.netResult)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {user.totalBetsCount}
                          {user.winningBetsCount > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              ({user.winningBetsCount} wins)
                            </Typography>
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography 
                          variant="body2" 
                          color={user.winRate > 50 ? 'success.main' : 'text.primary'}
                          fontWeight="bold"
                        >
                          {formatPercentage(user.winRate)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {formatDate(user.lastBetDate)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {formatDate(user.lastLoginAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {reportData && (
              <TablePagination
                component="div"
                count={reportData.totalCount}
                page={filters.page - 1}
                onPageChange={(_, newPage) => handleFilterChange('page', newPage + 1)}
                rowsPerPage={filters.pageSize}
                onRowsPerPageChange={(e) => {
                  handleFilterChange('pageSize', parseInt(e.target.value));
                  handleFilterChange('page', 1);
                }}
                rowsPerPageOptions={[10, 20, 50, 100]}
              />
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default UserReports;